import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from './config';

// Helper function to convert YouTube URL to embed URL
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  
  // Check if it's already an embed URL
  if (url.includes('youtube.com/embed/') || url.includes('youtu.be/embed/')) {
    return url;
  }
  
  // Extract video ID from various YouTube URL formats
  let videoId = null;
  
  // Format: https://www.youtube.com/watch?v=VIDEO_ID
  const match1 = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  if (match1 && match1[1]) {
    videoId = match1[1];
  }
  
  // If we extracted a video ID, return the embed URL
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }
  
  // If it's already a valid URL, return as is
  if (url.startsWith('http')) {
    return url;
  }
  
  return null;
};

const ListeningAssignmentViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [assignment, setAssignment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    loadAssignment();
  }, [id]);

  const loadAssignment = async () => {
    try {
      const response = await axios.get(buildApiUrl(`/api/listening/assignments/${id}/details`));
      if (response.data.success) {
        setAssignment(response.data.assignment);
        setQuestions(response.data.assignment.questions || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading assignment:', error);
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionIndex, optionIndex) => {
    setAnswers({
      ...answers,
      [questionIndex]: optionIndex
    });
  };

  const handleSubmit = () => {
    // Calculate score
    let correctCount = 0;
    questions.forEach((question, index) => {
      if (answers[index] === question.correctAnswer) {
        correctCount++;
      }
    });
    
    setScore(correctCount);
    setSubmitted(true);
    
    // TODO: Save submission to backend
    // axios.post(buildApiUrl('/api/listening/submissions'), {
    //   assignmentId: id,
    //   studentId: localStorage.getItem('schoolId'),
    //   answers: answers,
    //   score: correctCount,
    //   total: questions.length
    // });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🎧</div>
          <div className="text-xl text-gray-600">Loading assignment...</div>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <div className="text-xl text-gray-600">Assignment not found</div>
          <button
            onClick={() => navigate('/student-dashboard')}
            className="mt-4 px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="text-8xl mb-6">
              {score >= questions.length * 0.8 ? '🎉' : score >= questions.length * 0.6 ? '👍' : '💪'}
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Assignment Completed!</h2>
            <div className="text-6xl font-bold text-pink-600 mb-4">
              {score}/{questions.length}
            </div>
            <p className="text-xl text-gray-600 mb-2">
              {Math.round((score / questions.length) * 100)}% Correct
            </p>
            <p className="text-gray-500 mb-8">
              {score >= questions.length * 0.8 
                ? 'Excellent work!' 
                : score >= questions.length * 0.6 
                ? 'Good job!' 
                : 'Keep practicing!'}
            </p>
            
            {/* Show Results */}
            <div className="text-left mb-8 space-y-4">
              {questions.map((question, qIndex) => {
                const studentAnswer = answers[qIndex];
                const isCorrect = studentAnswer === question.correctAnswer;
                
                return (
                  <div key={qIndex} className={`p-4 rounded-lg border-2 ${
                    isCorrect ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
                  }`}>
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-2xl">{isCorrect ? '✅' : '❌'}</span>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800 mb-2">
                          Question {qIndex + 1}: {question.question}
                        </p>
                        <div className="space-y-1 text-sm">
                          <p className="text-gray-600">
                            Your answer: <span className={isCorrect ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                              {question.options[studentAnswer]}
                            </span>
                          </p>
                          {!isCorrect && (
                            <p className="text-gray-600">
                              Correct answer: <span className="text-green-600 font-medium">
                                {question.options[question.correctAnswer]}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <button
              onClick={() => navigate('/student-dashboard')}
              className="px-8 py-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl hover:from-pink-600 hover:to-rose-700 transition-all duration-300 shadow-lg font-semibold text-lg"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/student-dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
            >
              <span className="text-xl">←</span>
              <span>Back</span>
            </button>
            <div className="flex items-center gap-2 text-pink-600">
              <span className="text-2xl">🎧</span>
              <span className="font-semibold">Listening Assignment</span>
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{assignment.title}</h1>
          {assignment.description && (
            <p className="text-gray-600">{assignment.description}</p>
          )}
        </div>

        {/* Media Player */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span>🎵</span>
            Listen Carefully
          </h2>
          
          {assignment.media_type === 'video' && assignment.video_link && (
            <div className="aspect-video rounded-lg overflow-hidden bg-black">
              <iframe
                src={getYouTubeEmbedUrl(assignment.video_link)}
                className="w-full h-full"
                allowFullScreen
                title="Listening Video"
              />
            </div>
          )}
          
          {(assignment.media_type === 'audio' || assignment.media_type === 'record' || assignment.media_type === 'upload-video') && assignment.media_url && (
            <div className="bg-gradient-to-r from-pink-100 to-purple-100 rounded-xl p-8 text-center">
              {assignment.media_type === 'upload-video' ? (
                <video
                  ref={videoRef}
                  controls
                  className="w-full rounded-lg"
                  src={buildApiUrl(assignment.media_url)}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <>
                  <div className="text-6xl mb-4">🎧</div>
                  <audio
                    ref={audioRef}
                    controls
                    className="w-full"
                    src={buildApiUrl(assignment.media_url)}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </>
              )}
            </div>
          )}
          
          <p className="text-sm text-gray-500 mt-4 text-center">
            💡 You can replay the audio/video as many times as needed
          </p>
        </div>

        {/* Questions */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <span>📝</span>
            Answer the Questions
          </h2>
          
          <div className="space-y-6">
            {questions.map((question, qIndex) => (
              <div key={qIndex} className="border-b border-gray-200 pb-6 last:border-0">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  {qIndex + 1}. {question.question}
                </p>
                
                <div className="space-y-2">
                  {question.options?.map((option, oIndex) => (
                    <label
                      key={oIndex}
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        answers[qIndex] === oIndex
                          ? 'border-pink-500 bg-pink-50'
                          : 'border-gray-200 hover:border-pink-300 hover:bg-pink-50/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${qIndex}`}
                        checked={answers[qIndex] === oIndex}
                        onChange={() => handleAnswerChange(qIndex, oIndex)}
                        className="w-5 h-5 text-pink-500"
                      />
                      <span className="text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Questions answered: {Object.keys(answers).length} / {questions.length}
            </div>
            <button
              onClick={handleSubmit}
              disabled={Object.keys(answers).length !== questions.length}
              className="px-8 py-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl hover:from-pink-600 hover:to-rose-700 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg"
            >
              Submit Assignment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListeningAssignmentViewer;
